import { StorageError } from '../app/errors';
import { requireTransaction } from './commit';
import { db, TABLE } from './db';
import type { AudioBlobRow } from './db';

/**
 * DD-2 / design §8.3 — **audio lives in IndexedDB, in a dedicated `audioBlobs` object store, behind
 * this interface.**
 *
 * Three properties the design buys with that choice, all of which this module has to preserve:
 *
 * 1. **INV-8's cascade is atomic across rows *and* audio.** Both live in the same IndexedDB, so one
 *    transaction spans them. That only holds if blob deletion joins the caller's transaction rather
 *    than opening its own — hence `requireTransaction` on every mutator here.
 * 2. **Multi-megabyte writes stay out of the tap transaction** (§5.1 step 7: "exactly two rows, no
 *    blob"). Nothing in this module is ever in an `Increment`'s scope.
 * 3. **AUD-1 forbids transcoding on any path.** `finalizeBlob` is a `new Blob([...])` over the raw
 *    parts in arrival order and nothing else — no re-muxing, no header rewriting. Build unit U-1
 *    settled that this is what iOS Safari can play back (assumption A-2); if it could not, the
 *    correct finding was that A-2 is false, not a fix-up step added here.
 *
 * ── Transaction discipline ───────────────────────────────────────────────────
 *
 * **No function here opens a transaction.** Mutators join the ambient one and throw if there is
 * none. Design §8.2 step 2's "in its own transaction on the `audioBlobs` store only" is expressed
 * at the *call site* — `write([TABLE.audioBlobs], () => appendChunk(...))` — where the scope is
 * visible, rather than hidden inside a helper where a caller cannot tell what it joined.
 *
 * ── Relationship to `audioSpikeStore.ts` ─────────────────────────────────────
 *
 * Build unit U-1 declared `data/audioSpikeStore.ts` as a thin slice of this module. It is
 * **superseded, not absorbed**: it writes to the *harness* database's own v002 stores and exists to
 * settle assumption A-2 with per-chunk timing records the product does not keep. This module is the
 * product's only blob path. When the harness is retired, `audioSpikeStore.ts` goes with it.
 */

/** What a caller supplies to store an already-complete recording (import, restore, BAK-2). */
export interface CompleteBlobInput {
  id: string;
  blob: Blob;
  mime: string;
  createdAt: number;
}

/**
 * Writes a complete blob under a caller-supplied id.
 *
 * The id is minted by the caller because design §8.2 mints it at recording **start**, before any
 * bytes exist, and stamps it onto the `Observation` in the same transaction. A store that minted its
 * own id would make that impossible and force the Observation to be mutated later, which INV-1
 * forbids.
 *
 * @throws {StorageError} outside a transaction, or on a write failure.
 */
export async function putBlob(input: CompleteBlobInput): Promise<void> {
  requireTransaction('putBlob');
  const row: AudioBlobRow = {
    id: input.id,
    blob: input.blob,
    chunks: [],
    mime: input.mime,
    bytes: input.blob.size,
    complete: true,
    createdAt: input.createdAt,
  };
  try {
    await db().audioBlobs.put(row);
  } catch (cause) {
    throw new StorageError('Could not store the audio recording.', {
      cause,
      context: { store: TABLE.audioBlobs, id: input.id },
    });
  }
}

/** BAK-2 — one audio row exactly as an archive recorded it, bytes and metadata both. */
export interface ImportedBlobInput {
  id: string;
  /**
   * The bytes from `blobs/<id>.<ext>`, or `null` when the archive listed the row and carried no
   * file for it (the export's `report.missingBlobBytes`). The row is still restored: D-19's
   * "something happened at minute 34" lives on the Observation, and the archive's `bytes` field is
   * what the next export re-emits — so an invented empty blob would add a `blobs/` entry the source
   * archive did not have and BAK-2's round trip would stop being byte-stable.
   */
  blob: Blob | null;
  /** `AudioBlob.mime` from `data.json`, never guessed from the file extension (AUD-1, §11.7). */
  mime: string;
  /** The archived byte count. Preserved verbatim — see below. */
  bytes: number;
  /** The archived finalize state. Preserved verbatim — see below. */
  complete: boolean;
  createdAt: number;
}

/**
 * Restores one audio row from a backup, **preserving `complete` and `bytes` exactly**.
 *
 * Its own function rather than a flag on `putBlob`, because the two differ on the one thing that
 * matters here. `putBlob` writes a *finished* recording: it stamps `complete = true` and recomputes
 * `bytes` from the blob, which is right for a caller that has just assembled one. An import has no
 * such authority — §17.4's fixture requires an **incomplete** blob (finalize never ran, §8.5's hard
 * jetsam case) to survive a restore as incomplete, and BAK-2's round trip is byte-stable only if
 * `complete` and `bytes` come back the way they went in. Routing an import through `putBlob` would
 * silently mark every recovered fragment as a finished recording and change the next export's bytes.
 *
 * An incomplete row is restored with its bytes in `chunks` and `blob = null`, which is the shape
 * `appendChunk` leaves and the shape §8.5's recovery pass expects to find.
 *
 * @throws {StorageError} outside a transaction, on a duplicate id, or on a write failure.
 */
export async function putImportedBlob(input: ImportedBlobInput): Promise<void> {
  requireTransaction('putImportedBlob');
  // Complete → the assembled blob, as `finalizeBlob` leaves it.
  // Incomplete → the bytes back in `chunks` with `blob = null`, which is what `appendChunk` leaves
  // and what §8.5's `pageshow` recovery pass expects to find.
  const row: AudioBlobRow = {
    id: input.id,
    blob: input.complete ? input.blob : null,
    chunks: !input.complete && input.blob ? [input.blob] : [],
    mime: input.mime,
    bytes: input.bytes,
    complete: input.complete,
    createdAt: input.createdAt,
  };
  try {
    await db().audioBlobs.add(row);
  } catch (cause) {
    throw new StorageError('Could not restore the audio recording from the backup.', {
      cause,
      context: { store: TABLE.audioBlobs, id: input.id },
    });
  }
}

/**
 * Creates the empty row for a recording that has just started (§8.2 step 1).
 *
 * `complete = false` and no bytes. §8.5's `pageshow` recovery pass finds exactly these rows after a
 * hard jetsam kill and assembles whatever chunks did land.
 *
 * @throws {StorageError} outside a transaction, or on a write failure.
 */
export async function openBlob(id: string, createdAt: number): Promise<void> {
  requireTransaction('openBlob');
  const row: AudioBlobRow = {
    id,
    blob: null,
    chunks: [],
    mime: '',
    bytes: 0,
    complete: false,
    createdAt,
  };
  try {
    await db().audioBlobs.add(row);
  } catch (cause) {
    throw new StorageError('Could not start the recording’s audio row.', {
      cause,
      context: { store: TABLE.audioBlobs, id },
    });
  }
}

/**
 * Appends one `dataavailable` payload (§8.2 step 2).
 *
 * Chunks accumulate in arrival order in `chunks` and are concatenated once, at finalize. Appending
 * to an array rather than re-concatenating the whole blob keeps each append O(1) in bytes; a
 * `VOICE_NOTE_MAX_S` note produces about `VOICE_NOTE_MAX_S / (TIMESLICE_MS/1000)` of them.
 *
 * IndexedDB serializes `readwrite` transactions over the same store, so two appends cannot
 * interleave and the array order *is* arrival order. (Build unit U-1 found that chunk writes can
 * otherwise complete out of order, which is why the order is recorded rather than inferred.)
 *
 * @param mime `recorder.mimeType`. Recorded on the first chunk that carries one; AUD-1 stores what
 *   was emitted, and U-1 found `mimeType` is legally `''` before `start()`.
 * @throws {StorageError} outside a transaction, if the row is missing, or on a write failure. Never
 *   swallowed: a silently dropped chunk is a hole in the middle of a recording that nothing
 *   downstream can detect (NFR-8).
 */
export async function appendChunk(id: string, chunk: Blob, mime?: string): Promise<void> {
  requireTransaction('appendChunk');
  const table = db().audioBlobs;
  let row: AudioBlobRow | undefined;
  try {
    row = await table.get(id);
  } catch (cause) {
    throw new StorageError('Could not read the audio row to append a chunk.', {
      cause,
      context: { store: TABLE.audioBlobs, id },
    });
  }
  if (!row) {
    throw new StorageError(
      `No audio row exists for ${id}; the recording’s Observation was written without opening its blob.`,
      { context: { store: TABLE.audioBlobs, id } },
    );
  }
  if (row.complete) {
    throw new StorageError(
      `Audio ${id} is already finalized; appending would change a recording that has been assembled.`,
      { context: { store: TABLE.audioBlobs, id } },
    );
  }
  try {
    await table.put({
      ...row,
      chunks: [...row.chunks, chunk],
      bytes: row.bytes + chunk.size,
      mime: row.mime === '' && mime ? mime : row.mime,
    });
  } catch (cause) {
    throw new StorageError(
      `Could not persist an audio chunk for ${id}. The recording now has a gap.`,
      { cause, context: { store: TABLE.audioBlobs, id } },
    );
  }
}

/**
 * Assembles the chunks verbatim and marks the recording complete (§8.2 step 3).
 *
 * Idempotent: finalizing an already-complete row returns it unchanged, so AUD-3's several finalize
 * triggers (user stop · auto-stop · `onerror` · track `ended` · `pagehide` · `visibilitychange` ·
 * any state transition) can race without producing two different blobs.
 *
 * A recording with **no** chunks finalizes to a zero-length blob with `complete = true`, not to an
 * error: AUD-3 says captured audio is never discarded, and there is nothing to discard here. The
 * playability probe (§8.2) is what labels it "audio unavailable", and D-19's "something happened at
 * minute 34" survives on the Observation regardless.
 *
 * @throws {StorageError} outside a transaction, if the row is missing, or on a write failure.
 */
export async function finalizeBlob(id: string, mime?: string): Promise<AudioBlobRow> {
  requireTransaction('finalizeBlob');
  const table = db().audioBlobs;
  const row = await table.get(id);
  if (!row) {
    throw new StorageError(`No audio row exists for ${id}; nothing to finalize.`, {
      context: { store: TABLE.audioBlobs, id },
    });
  }
  if (row.complete) return row;

  const effectiveMime = row.mime !== '' ? row.mime : (mime ?? '');
  // AUD-1: verbatim concatenation and nothing else.
  const assembled = new Blob(row.chunks, { type: effectiveMime });
  const finalized: AudioBlobRow = {
    ...row,
    blob: assembled,
    chunks: [],
    mime: effectiveMime,
    bytes: assembled.size,
    complete: true,
  };
  try {
    await table.put(finalized);
  } catch (cause) {
    throw new StorageError(`Could not finalize audio ${id}.`, {
      cause,
      context: { store: TABLE.audioBlobs, id },
    });
  }
  return finalized;
}

/** @throws {StorageError} on a read failure. */
export async function getBlob(id: string): Promise<AudioBlobRow | undefined> {
  try {
    return await db().audioBlobs.get(id);
  } catch (cause) {
    throw new StorageError('Could not read the audio recording.', {
      cause,
      context: { store: TABLE.audioBlobs, id },
    });
  }
}

/**
 * Reads a set of blobs by id.
 *
 * Ids with no row come back **absent**, not as `undefined` holes — a dangling `audioBlobId` is a
 * condition OBS-2's orphan report exists to surface (§1.4), and a caller that wants to detect one
 * compares lengths rather than filtering nulls it might forget to filter.
 *
 * @throws {StorageError} on a read failure.
 */
export async function getBlobs(ids: readonly string[]): Promise<AudioBlobRow[]> {
  if (ids.length === 0) return [];
  try {
    const rows = await db().audioBlobs.bulkGet([...ids]);
    return rows.filter((row): row is AudioBlobRow => row !== undefined);
  } catch (cause) {
    throw new StorageError('Could not read the audio recordings.', {
      cause,
      context: { store: TABLE.audioBlobs, count: ids.length },
    });
  }
}

/**
 * Every audio row, oldest first.
 *
 * Ordered by the indexed `createdAt` so the archive worker (§11.2) can walk blobs one at a time in a
 * stable order with yields between them, rather than materializing the whole store.
 *
 * @throws {StorageError} on a read failure.
 */
export async function listBlobs(): Promise<AudioBlobRow[]> {
  try {
    return await db().audioBlobs.orderBy('createdAt').toArray();
  } catch (cause) {
    throw new StorageError('Could not list audio recordings.', { cause });
  }
}

/**
 * §8.5's `pageshow` recovery input — every row finalize never ran on.
 *
 * Filtered in memory rather than by index: **IndexedDB does not accept a boolean as a key**, so an
 * index on `complete` would be accepted and hold no entries at all, and this query would silently
 * return nothing after every crash. Build unit U-1 found that on `audioSegments.complete`.
 *
 * @throws {StorageError} on a read failure.
 */
export async function listIncompleteBlobs(): Promise<AudioBlobRow[]> {
  return (await listBlobs()).filter((row) => !row.complete);
}

/**
 * Deletes blobs by id, joining the caller's transaction.
 *
 * INV-8 requires that deleting a Match or Team removes dependent rows **and audio blobs in one
 * transaction**. That is only true if this joins the cascade's transaction, which the guard makes
 * structural rather than documented.
 *
 * @throws {StorageError} outside a transaction, or on a write failure.
 */
export async function deleteBlobs(ids: readonly string[]): Promise<void> {
  requireTransaction('deleteBlobs');
  if (ids.length === 0) return;
  try {
    await db().audioBlobs.bulkDelete([...ids]);
  } catch (cause) {
    throw new StorageError('Could not delete audio recordings during a cascade.', {
      cause,
      context: { store: TABLE.audioBlobs, count: ids.length },
    });
  }
}

/**
 * Total stored audio bytes — NFR-4's per-match sizing and BAK-3's storage report.
 *
 * Sums the recorded `bytes` column rather than reading every blob, so it does not pull megabytes
 * into memory to answer a number.
 *
 * @throws {StorageError} on a read failure.
 */
export async function totalBlobBytes(): Promise<number> {
  try {
    let total = 0;
    await db().audioBlobs.each((row) => {
      total += row.bytes;
    });
    return total;
  } catch (cause) {
    throw new StorageError('Could not total stored audio bytes.', { cause });
  }
}
